import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const MagicLinkVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing token');
      return;
    }

    // Check if user is already logged in
    const existingToken = localStorage.getItem('token');
    const existingUser = localStorage.getItem('user');

    if (existingToken && existingUser) {
      console.log('User already logged in, redirecting to dashboard');
      setStatus('success');
      setMessage('Already logged in! Redirecting...');
      setTimeout(() => navigate('/dashboard'), 1000);
      return;
    }

    // Use a unique key for this token
    const verifiedTokenKey = `magic_token_${token}`;

    // Check if verification is already in progress or completed for this token
    const existingStatus = sessionStorage.getItem(verifiedTokenKey);

    if (existingStatus === 'in_progress') {
      console.log('Verification already in progress, skipping duplicate call');
      return;
    }

    if (existingStatus === 'completed') {
      console.log('Token already verified, redirecting to dashboard');
      navigate('/dashboard');
      return;
    }

    // Mark as in progress BEFORE making the API call
    sessionStorage.setItem(verifiedTokenKey, 'in_progress');

    verifyToken(token, verifiedTokenKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async (token, verifiedTokenKey) => {
    try {
      const response = await api.post('/auth/verify-magic-link', { token });

      // Mark as completed
      sessionStorage.setItem(verifiedTokenKey, 'completed');

      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);

      setStatus('success');
      setMessage('Login successful! Redirecting...');

      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Invalid or expired link';

      // If the error is "already used", check if user is actually logged in
      if (errorMessage.includes('already been used')) {
        const existingToken = localStorage.getItem('token');
        const existingUser = localStorage.getItem('user');

        if (existingToken && existingUser) {
          // User is logged in, just redirect them
          console.log('Token already used but user is logged in, redirecting');
          setStatus('success');
          setMessage('Already logged in! Redirecting...');
          setTimeout(() => navigate('/dashboard'), 1000);
          return;
        }
      }

      // Only show error if this was the first attempt
      const currentStatus = sessionStorage.getItem(verifiedTokenKey);
      if (currentStatus === 'in_progress') {
        sessionStorage.setItem(verifiedTokenKey, 'failed');
        setStatus('error');
        setMessage(errorMessage);
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '3rem',
        textAlign: 'center'
      }}>
        {status === 'verifying' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              margin: '0 auto 1.5rem',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
              Verifying...
            </h2>
            <p style={{ margin: 0, color: '#666' }}>
              Please wait while we log you in
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: 'white'
            }}>
              ✓
            </div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
              Success!
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '1.1rem' }}>
              {message}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: 'white'
            }}>
              ✕
            </div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
              Login Failed
            </h2>
            <p style={{ margin: '0 0 2rem 0', color: '#666', fontSize: '1.1rem' }}>
              {message}
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '0.875rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify;
